// @ts-check

import ExifReader from "exifreader";
import { html, render } from "lit-html";
import { dAlert } from "./dialogs.js";
import { createRenderScheduler } from "./render_scheduler.js";
import { setupServiceWorkerReloadWhenIdle } from "./sw-reload.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {{ prompt: () => Promise<void> | void, userChoice: Promise<unknown> }} BeforeInstallPromptEvent */
/**
 * @typedef {{
 *   filename: string,
 *   type: string,
 *   size: number,
 *   lastModified: number,
 *   previewUrl: string,
 *   status: string,
 *   error: string,
 *   isDragging: boolean,
 *   isParsing: boolean,
 *   canInstall: boolean,
 *   installPrompt: BeforeInstallPromptEvent | null,
 *   tags: Record<string, any> | null,
 * }} AppState
 */

const root = document.getElementById("app");
const cachePrefix = "exiftool-cache-";
const debugTapThreshold = 7;
const debugTapWindowMs = 1500;

/** @type {AppState} */
const state = {
  filename: "",
  type: "",
  size: 0,
  lastModified: 0,
  previewUrl: "",
  status: "Drop an image to inspect the metadata stored inside it.",
  error: "",
  isDragging: false,
  isParsing: false,
  canInstall: false,
  installPrompt: null,
  tags: null,
};

const debugTapState = { count: 0, lastTapAt: 0 };

function main() {
  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);

  if (!(root instanceof HTMLElement)) {
    return;
  }

  root.addEventListener("click", handleClick);
  root.addEventListener("change", handleChange);
  renderApp();
  void setupServiceWorkerReloadWhenIdle({ isIdle: isAppIdle });
}

function renderApp() {
  if (!(root instanceof HTMLElement)) {
    return;
  }

  const view = appView();
  render(view, root);
}

const renderScheduler = createRenderScheduler(renderApp);

/**
 * @returns {Promise<void>}
 */
async function scheduleRender() {
  renderScheduler.requestUpdate();

  let renderComplete = false;
  while (!renderComplete) {
    renderComplete = await renderScheduler.getUpdateComplete();
  }
}

/**
 * @returns {LitView}
 */
function appView() {
  return html`
    <main
      class="app-shell mx-auto grid min-h-dvh w-full max-w-5xl gap-7 overflow-x-hidden"
    >
      <header
        class="grid gap-4 border-b border-stone-200 pb-5 sm:grid-cols-[1fr_auto] sm:items-start"
      >
        <div>
          <p
            class="text-xs font-semibold uppercase tracking-widest text-lime-700"
          >
            Local image metadata
          </p>
          <h1
            class="mt-2 text-3xl font-semibold tracking-normal text-stone-950"
            data-action="show-debug-info"
          >
            Exiftool
          </h1>
          <p class="mt-2 max-w-2xl text-sm leading-6 text-stone-500">
            ${state.status}
          </p>
        </div>
        ${state.canInstall ? renderInstallButton() : ""}
      </header>

      <section
        class="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:items-start"
      >
        <div class="grid min-w-0 gap-5">
          ${renderDropzone()} ${renderPreviewPanel()}
        </div>
        <div class="grid min-w-0 gap-5">
          ${state.error ? renderErrorPanel() : ""} ${renderSummaryPanel()}
          ${renderGpsPanel()} ${renderMetadataPanel()}
        </div>
      </section>
    </main>
  `;
}

/**
 * @returns {LitView}
 */
function renderInstallButton() {
  return html`
    <button
      class="h-10 rounded-lg bg-stone-950 px-4 text-sm font-semibold text-white transition hover:bg-stone-800"
      type="button"
      data-action="install"
    >
      Install
    </button>
  `;
}

/**
 * @returns {LitView}
 */
function renderDropzone() {
  const draggingClass = state.isDragging
    ? "border-lime-500 bg-lime-50 text-lime-800"
    : "border-stone-300 bg-white text-stone-600";

  return html`
    <label
      class="grid min-h-56 cursor-pointer place-items-center rounded-lg border-2 border-dashed p-6 text-center transition hover:border-lime-500 hover:bg-lime-50 hover:text-lime-800 ${draggingClass}"
      for="file-input"
      @dragenter=${handleDragEnter}
      @dragover=${handleDragOver}
      @dragleave=${handleDragLeave}
      @dragend=${handleDragLeave}
      @drop=${handleDrop}
    >
      <span>
        <span class="block text-base font-semibold text-stone-950">
          ${state.isParsing ? "Reading metadata..." : "Choose or drop an image"}
        </span>
        <span class="mt-2 block text-sm leading-6">
          JPEG, HEIC, AVIF, WebP, PNG, TIFF, GIF, and JPEG XL are supported by
          ExifReader.
        </span>
        ${state.filename
          ? html`<span
              class="mt-3 block break-all text-sm font-medium text-stone-900"
              >${state.filename}</span
            >`
          : ""}
      </span>
      <input id="file-input" class="sr-only" type="file" />
    </label>
  `;
}

/**
 * @returns {LitView}
 */
function renderPreviewPanel() {
  if (!state.previewUrl) {
    return renderEmptyPanel(
      "Preview",
      "The image preview appears here after selection.",
    );
  }

  return html`
    <section class="grid gap-3">
      <h2
        class="border-b border-stone-100 pb-2 text-xs font-semibold uppercase tracking-widest text-stone-500"
      >
        Preview
      </h2>
      <div
        class="max-w-full overflow-hidden rounded-lg border border-stone-200 bg-stone-100"
      >
        <img
          class="h-auto max-h-[26rem] w-full max-w-full object-contain"
          src=${state.previewUrl}
          alt=${state.filename}
        />
      </div>
    </section>
  `;
}

/**
 * @returns {LitView}
 */
function renderErrorPanel() {
  return html`
    <section
      class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
    >
      ${state.error}
    </section>
  `;
}

/**
 * @returns {LitView}
 */
function renderSummaryPanel() {
  if (!state.tags) {
    return renderEmptyPanel(
      "Summary",
      "File details and common camera fields appear here.",
    );
  }

  const rows = [
    ["File", state.filename],
    ["Type", getDisplayValue(state.type || getTagDescription("FileType"))],
    ["Size", formatBytes(state.size)],
    ["Modified", formatDate(state.lastModified)],
    ["Dimensions", getDimensionsText()],
    ["Camera", getCameraText()],
    ["Captured", getCaptureDateText()],
    ["Orientation", getTagDescription("Orientation")],
  ];

  return html`
    <section class="grid gap-4">
      <h2
        class="border-b border-stone-100 pb-2 text-xs font-semibold uppercase tracking-widest text-stone-500"
      >
        Summary
      </h2>
      <dl
        class="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-x-4 gap-y-2 sm:grid-cols-[8rem_minmax(0,1fr)]"
      >
        ${renderDetailRows(rows)}
      </dl>
    </section>
  `;
}

/**
 * @returns {LitView}
 */
function renderGpsPanel() {
  const gps = getGpsDetails();
  if (!state.tags) {
    return renderEmptyPanel(
      "GPS",
      "Coordinates appear here when the file contains location data.",
    );
  }

  if (!gps.hasCoordinates) {
    return renderEmptyPanel("GPS", "No GPS coordinates found in this image.");
  }

  return html`
    <section class="grid gap-4">
      <h2
        class="border-b border-stone-100 pb-2 text-xs font-semibold uppercase tracking-widest text-stone-500"
      >
        GPS
      </h2>
      <dl
        class="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-x-4 gap-y-2 sm:grid-cols-[8rem_minmax(0,1fr)]"
      >
        ${renderDetailRows([
          ["Latitude", gps.latitude],
          ["Longitude", gps.longitude],
          ["Altitude", gps.altitude],
        ])}
      </dl>
      <a
        class="text-sm font-semibold text-lime-700 underline decoration-lime-300 underline-offset-4 hover:text-lime-900"
        href=${gps.mapUrl}
        target="_blank"
        rel="noreferrer"
      >
        Open coordinates in maps
      </a>
    </section>
  `;
}

/**
 * @returns {LitView}
 */
function renderMetadataPanel() {
  if (!state.tags) {
    return renderEmptyPanel(
      "All metadata",
      "Raw ExifReader groups appear here.",
    );
  }

  const groupViews = getMetadataGroups();
  if (groupViews.length === 0) {
    return renderEmptyPanel("All metadata", "No metadata tags were found.");
  }

  return html`
    <section class="grid min-w-0 gap-4">
      <h2
        class="border-b border-stone-100 pb-2 text-xs font-semibold uppercase tracking-widest text-stone-500"
      >
        All metadata
      </h2>
      <div class="grid min-w-0 gap-3">${groupViews}</div>
    </section>
  `;
}

/**
 * @param {string} title
 * @param {string} text
 * @returns {LitView}
 */
function renderEmptyPanel(title, text) {
  return html`
    <section class="grid gap-3">
      <h2
        class="border-b border-stone-100 pb-2 text-xs font-semibold uppercase tracking-widest text-stone-500"
      >
        ${title}
      </h2>
      <p class="text-sm leading-6 text-stone-400">${text}</p>
    </section>
  `;
}

/**
 * @param {string[][]} rows
 * @returns {LitView[]}
 */
function renderDetailRows(rows) {
  /** @type {LitView[]} */
  const rowViews = [];
  for (let i = 0, total = rows.length; i < total; i += 1) {
    const row = rows[i];
    const value = row[1] || "Not found";
    rowViews.push(html`
      <dt class="text-sm text-stone-500">${row[0]}</dt>
      <dd class="min-w-0 break-words text-sm font-medium text-stone-900">
        ${value}
      </dd>
    `);
  }

  return rowViews;
}

/**
 * @returns {LitView[]}
 */
function getMetadataGroups() {
  /** @type {LitView[]} */
  const views = [];
  const tags = state.tags || {};
  const groupNames = Object.keys(tags).sort();

  for (let i = 0, total = groupNames.length; i < total; i += 1) {
    const groupName = groupNames[i];
    const groupValue = tags[groupName];
    if (!shouldRenderMetadataGroup(groupName, groupValue)) {
      continue;
    }

    views.push(renderMetadataGroup(groupName, groupValue));
  }

  return views;
}

/**
 * @param {string} groupName
 * @param {unknown} groupValue
 * @returns {boolean}
 */
function shouldRenderMetadataGroup(groupName, groupValue) {
  let result = true;

  if (groupName === "Thumbnail") {
    result = false;
    return result;
  }

  if (groupValue === null || typeof groupValue !== "object") {
    result = false;
  }

  return result;
}

/**
 * @param {string} groupName
 * @param {unknown} groupValue
 * @returns {LitView}
 */
function renderMetadataGroup(groupName, groupValue) {
  const rows = getGroupRows(groupValue);

  return html`
    <details class="min-w-0 rounded-lg border border-stone-200 bg-white" open>
      <summary
        class="cursor-pointer px-4 py-3 text-sm font-semibold text-stone-900"
      >
        ${formatGroupName(groupName)}
        <span class="ml-2 text-xs font-medium text-stone-400"
          >${rows.length} tags</span
        >
      </summary>
      <div class="border-t border-stone-100">${renderMetadataRows(rows)}</div>
    </details>
  `;
}

/**
 * @param {Array<{ name: string, value: string }>} rows
 * @returns {LitView[]}
 */
function renderMetadataRows(rows) {
  /** @type {LitView[]} */
  const views = [];
  for (let i = 0, total = rows.length; i < total; i += 1) {
    const row = rows[i];
    views.push(html`
      <div
        class="grid gap-1 border-b border-stone-100 px-4 py-3 last:border-b-0 sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-4"
      >
        <div
          class="min-w-0 break-words text-xs font-semibold uppercase tracking-wider text-stone-400"
        >
          ${row.name}
        </div>
        <pre
          class="min-w-0 max-w-full whitespace-pre-wrap break-all font-mono text-xs leading-5 text-stone-800"
        >
${row.value}</pre
        >
      </div>
    `);
  }

  return views;
}

/**
 * @param {unknown} groupValue
 * @returns {Array<{ name: string, value: string }>}
 */
function getGroupRows(groupValue) {
  /** @type {Array<{ name: string, value: string }>} */
  const rows = [];
  if (groupValue === null || typeof groupValue !== "object") {
    return rows;
  }

  const record = /** @type {Record<string, unknown>} */ (groupValue);
  const names = Object.keys(record).sort();
  for (let i = 0, total = names.length; i < total; i += 1) {
    const name = names[i];
    const value = record[name];
    rows.push({ name, value: stringifyTag(value) });
  }

  return rows;
}

/**
 * @param {Event} event
 * @returns {void}
 */
function handleClick(event) {
  const actionElement = getActionElement(event.target);
  if (actionElement === null) {
    return;
  }

  const action = actionElement.dataset.action || "";
  if (action === "install") {
    void promptInstall();
    return;
  }

  if (action === "show-debug-info") {
    void handleDebugTap();
  }
}

/**
 * @param {Event} event
 * @returns {void}
 */
function handleChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  if (target.id !== "file-input") {
    return;
  }

  const file = target.files ? target.files[0] : null;
  if (!file) {
    return;
  }

  void parseFile(file);
  target.value = "";
}

/**
 * @param {DragEvent} event
 * @returns {void}
 */
function handleDragEnter(event) {
  event.preventDefault();
  state.isDragging = true;
  scheduleRender();
}

/**
 * @param {DragEvent} event
 * @returns {void}
 */
function handleDragOver(event) {
  event.preventDefault();
  state.isDragging = true;
}

/**
 * @param {DragEvent} event
 * @returns {void}
 */
function handleDragLeave(event) {
  event.preventDefault();
  state.isDragging = false;
  scheduleRender();
}

/**
 * @param {DragEvent} event
 * @returns {void}
 */
function handleDrop(event) {
  event.preventDefault();
  state.isDragging = false;

  const files = event.dataTransfer ? event.dataTransfer.files : null;
  const file = files ? files[0] : null;
  if (!file) {
    scheduleRender();
    return;
  }

  void parseFile(file);
}

/**
 * @param {File} file
 * @returns {Promise<void>}
 */
async function parseFile(file) {
  setFileLoadingState(file);

  try {
    const tags = await ExifReader.load(file, {
      async: true,
      computed: true,
      expanded: true,
      includeUnknown: true,
      excludeTags: { mpf: true },
    });
    state.tags = /** @type {Record<string, any>} */ (tags);
    state.error = "";
    state.status = getParsedStatus();
  } catch (error) {
    state.tags = null;
    state.error = getErrorMessage(error);
    state.status = "The file could not be parsed.";
  } finally {
    state.isParsing = false;
    scheduleRender();
  }
}

/**
 * @param {File} file
 * @returns {void}
 */
function setFileLoadingState(file) {
  if (state.previewUrl) {
    URL.revokeObjectURL(state.previewUrl);
  }

  state.filename = file.name;
  state.type = file.type;
  state.size = file.size;
  state.lastModified = file.lastModified;
  state.previewUrl = URL.createObjectURL(file);
  state.status = "Parsing metadata locally in this browser.";
  state.error = "";
  state.isParsing = true;
  state.tags = null;
  scheduleRender();
}

/**
 * @returns {string}
 */
function getParsedStatus() {
  const tagCount = getTotalTagCount();
  const suffix = tagCount === 1 ? "tag" : "tags";
  const result = `Found ${tagCount} metadata ${suffix}.`;
  return result;
}

/**
 * @returns {number}
 */
function getTotalTagCount() {
  let count = 0;
  const tags = state.tags || {};
  const groupNames = Object.keys(tags);

  for (let i = 0, total = groupNames.length; i < total; i += 1) {
    const groupName = groupNames[i];
    const groupValue = tags[groupName];
    if (!shouldRenderMetadataGroup(groupName, groupValue)) {
      continue;
    }

    count += Object.keys(groupValue).length;
  }

  return count;
}

/**
 * @returns {string}
 */
function getDimensionsText() {
  const width = getFirstTagText([
    "Image Width",
    "ImageWidth",
    "PixelXDimension",
  ]);
  const height = getFirstTagText([
    "Image Height",
    "ImageHeight",
    "PixelYDimension",
  ]);
  let result = "";

  if (width && height) {
    result = `${width} x ${height}`;
  }

  return result;
}

/**
 * @returns {string}
 */
function getCameraText() {
  const make = getFirstTagText(["Make"]);
  const model = getFirstTagText(["Model"]);
  let result = "";

  if (make && model) {
    result = `${make} ${model}`;
    return result;
  }

  if (model) {
    result = model;
    return result;
  }

  result = make;
  return result;
}

/**
 * @returns {string}
 */
function getCaptureDateText() {
  const result = getFirstTagText([
    "DateTimeOriginal",
    "CreateDate",
    "DateTimeDigitized",
    "DateTime",
  ]);
  return result;
}

/**
 * @param {string[]} names
 * @returns {string}
 */
function getFirstTagText(names) {
  let result = "";

  for (let i = 0, total = names.length; i < total; i += 1) {
    const name = names[i];
    const value = getTagDescription(name);
    if (!value) {
      continue;
    }

    result = value;
    break;
  }

  return result;
}

/**
 * @param {string} name
 * @returns {string}
 */
function getTagDescription(name) {
  let result = "";
  const tag = findTag(name);

  if (tag && typeof tag === "object") {
    const record = /** @type {Record<string, unknown>} */ (tag);
    if (typeof record.description === "string") {
      result = record.description;
      return result;
    }

    if (record.computed !== undefined) {
      result = stringifySimpleValue(record.computed);
      return result;
    }

    if (record.value !== undefined) {
      result = stringifySimpleValue(record.value);
    }
  }

  return result;
}

/**
 * @param {string} name
 * @returns {unknown}
 */
function findTag(name) {
  let result = null;
  const tags = state.tags || {};
  const groupNames = Object.keys(tags);

  for (let i = 0, total = groupNames.length; i < total; i += 1) {
    const group = tags[groupNames[i]];
    if (group === null || typeof group !== "object") {
      continue;
    }

    const record = /** @type {Record<string, unknown>} */ (group);
    if (record[name] === undefined) {
      continue;
    }

    result = record[name];
    break;
  }

  return result;
}

/**
 * @returns {{ hasCoordinates: boolean, latitude: string, longitude: string, altitude: string, mapUrl: string }}
 */
function getGpsDetails() {
  const latitudeValue = getGpsNumber("Latitude");
  const longitudeValue = getGpsNumber("Longitude");
  const altitudeValue = getGpsNumber("Altitude");
  const result = {
    hasCoordinates: false,
    latitude: "",
    longitude: "",
    altitude: "",
    mapUrl: "",
  };

  if (latitudeValue === null || longitudeValue === null) {
    return result;
  }

  result.hasCoordinates = true;
  result.latitude = latitudeValue.toFixed(6);
  result.longitude = longitudeValue.toFixed(6);
  result.altitude =
    altitudeValue === null ? "Not found" : `${altitudeValue.toFixed(2)} m`;
  result.mapUrl = `https://www.google.com/maps/search/?api=1&query=${result.latitude},${result.longitude}`;
  return result;
}

/**
 * @param {string} name
 * @returns {number | null}
 */
function getGpsNumber(name) {
  let result = null;
  const gpsGroup = state.tags ? state.tags.gps : null;

  if (gpsGroup && typeof gpsGroup === "object") {
    const gpsRecord = /** @type {Record<string, unknown>} */ (gpsGroup);
    const gpsValue = gpsRecord[name];
    if (typeof gpsValue === "number" && Number.isFinite(gpsValue)) {
      result = gpsValue;
    }
  }

  return result;
}

/**
 * @param {unknown} tag
 * @returns {string}
 */
function stringifyTag(tag) {
  let result = stringifySimpleValue(tag);

  if (tag && typeof tag === "object") {
    const record = /** @type {Record<string, unknown>} */ (tag);
    if (record.description !== undefined) {
      result = stringifySimpleValue(record.description);
      return result;
    }

    if (record.computed !== undefined) {
      result = stringifySimpleValue(record.computed);
      return result;
    }

    if (record.value !== undefined) {
      result = stringifySimpleValue(record.value);
    }
  }

  return result;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function stringifySimpleValue(value) {
  let result = "";

  if (value === undefined) {
    result = "";
    return result;
  }

  if (typeof value === "string") {
    result = value;
    return result;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    result = String(value);
    return result;
  }

  try {
    result = JSON.stringify(value, getJsonReplacer(), 2);
  } catch (error) {
    result = String(value);
  }

  return result;
}

/**
 * @returns {(key: string, value: unknown) => unknown}
 */
function getJsonReplacer() {
  return function replaceJsonValue(key, value) {
    let result = value;
    if (value instanceof Uint8Array) {
      result = `[Uint8Array ${value.byteLength} bytes]`;
    }

    if (value instanceof ArrayBuffer) {
      result = `[ArrayBuffer ${value.byteLength} bytes]`;
    }

    return result;
  };
}

/**
 * @param {string} value
 * @returns {string}
 */
function getDisplayValue(value) {
  const result = value || "Not found";
  return result;
}

/**
 * @param {string} name
 * @returns {string}
 */
function formatGroupName(name) {
  let result = name;
  if (name.length > 0) {
    result = name[0].toUpperCase() + name.slice(1);
  }

  return result;
}

/**
 * @param {number} bytes
 * @returns {string}
 */
function formatBytes(bytes) {
  let result = "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value = value / 1024;
    unitIndex += 1;
  }

  if (unitIndex === 0) {
    result = `${value} ${units[unitIndex]}`;
  } else {
    result = `${value.toFixed(1)} ${units[unitIndex]}`;
  }

  return result;
}

/**
 * @param {number} timestamp
 * @returns {string}
 */
function formatDate(timestamp) {
  let result = "";

  if (timestamp > 0) {
    const date = new Date(timestamp);
    result = date.toLocaleString();
  }

  return result;
}

async function handleDebugTap() {
  const now = Date.now();
  const withinWindow = now - debugTapState.lastTapAt <= debugTapWindowMs;
  debugTapState.count = withinWindow ? debugTapState.count + 1 : 1;
  debugTapState.lastTapAt = now;

  if (debugTapState.count < debugTapThreshold) {
    return;
  }

  debugTapState.count = 0;
  await showDebugInfo();
}

async function showDebugInfo() {
  const cacheNames = await getAppCacheNames();
  const bundleUrls = await getCachedBundleUrls(cacheNames);
  const serviceWorkerState = await getServiceWorkerState();
  const standalone = isStandalonePwa();
  const lines = [
    `Online: ${navigator.onLine}`,
    `PWA standalone: ${standalone}`,
    `SW: ${serviceWorkerState}`,
    `Caches: ${cacheNames.join(", ") || "(none)"}`,
    `Bundle: ${bundleUrls.join("\n        ") || "(not in any cache)"}`,
    `File: ${state.filename || "(none)"}`,
    `Tags: ${state.tags ? getTotalTagCount() : 0}`,
    `URL: ${window.location.href}`,
    `UA: ${navigator.userAgent}`,
  ];

  await dAlert({ message: lines.join("\n") });
}

/**
 * @returns {Promise<string[]>}
 */
async function getAppCacheNames() {
  let cacheNames = [];

  try {
    const allCacheNames = await caches.keys();
    cacheNames = allCacheNames.filter((name) => name.startsWith(cachePrefix));
  } catch (error) {
    cacheNames = [`(caches.keys failed: ${getErrorMessage(error)})`];
  }

  return cacheNames;
}

/**
 * @param {string[]} cacheNames
 * @returns {Promise<string[]>}
 */
async function getCachedBundleUrls(cacheNames) {
  const bundleUrls = [];

  try {
    for (let i = 0, total = cacheNames.length; i < total; i += 1) {
      const cacheName = cacheNames[i];
      if (!cacheName.startsWith(cachePrefix)) {
        continue;
      }

      const cache = await caches.open(cacheName);
      const requests = await cache.keys();
      for (
        let j = 0, requestTotal = requests.length;
        j < requestTotal;
        j += 1
      ) {
        const request = requests[j];
        if (request.url.includes("app.bundle.js")) {
          bundleUrls.push(`[${cacheName}] ${request.url}`);
        }
      }
    }
  } catch (error) {
    bundleUrls.push(`(cache inspection failed: ${getErrorMessage(error)})`);
  }

  return bundleUrls;
}

/**
 * @returns {Promise<string>}
 */
async function getServiceWorkerState() {
  let serviceWorkerState = "(unsupported)";

  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      serviceWorkerState = getRegistrationState(registration);
    }
  } catch (error) {
    serviceWorkerState = `(getRegistration failed: ${getErrorMessage(error)})`;
  }

  return serviceWorkerState;
}

/**
 * @param {ServiceWorkerRegistration | undefined} registration
 * @returns {string}
 */
function getRegistrationState(registration) {
  let registrationState = "(no registration)";

  if (registration !== undefined) {
    const active = registration.active ? registration.active.state : "none";
    const waiting = registration.waiting ? registration.waiting.state : "none";
    const installing = registration.installing
      ? registration.installing.state
      : "none";
    registrationState = `active=${active} waiting=${waiting} installing=${installing}`;
  }

  return registrationState;
}

function isStandalonePwa() {
  let standalone = false;

  if (window.matchMedia) {
    standalone = window.matchMedia("(display-mode: standalone)").matches;
  }

  return standalone;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function getErrorMessage(error) {
  let message = String(error);

  if (error instanceof Error) {
    message = error.message;
  }

  return message;
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function getActionElement(target) {
  let actionElement = null;

  if (target instanceof HTMLElement) {
    const closestElement = target.closest("[data-action]");
    if (closestElement instanceof HTMLElement) {
      actionElement = closestElement;
    }
  }

  return actionElement;
}

/**
 * @param {Event} event
 * @returns {void}
 */
function handleBeforeInstallPrompt(event) {
  event.preventDefault();
  state.installPrompt = /** @type {BeforeInstallPromptEvent} */ (
    /** @type {unknown} */ (event)
  );
  state.canInstall = true;
  scheduleRender();
}

function handleAppInstalled() {
  state.installPrompt = null;
  state.canInstall = false;
  scheduleRender();
}

async function promptInstall() {
  const promptEvent = state.installPrompt;
  if (promptEvent === null) {
    return;
  }

  state.installPrompt = null;
  state.canInstall = false;
  scheduleRender();

  try {
    await promptEvent.prompt();
    await promptEvent.userChoice;
  } catch (error) {
    console.warn("[app] install prompt failed", error);
  }
}

function isAppIdle() {
  const hasOpenFile = state.filename !== "" || state.tags !== null;
  const result = !state.isParsing && !hasOpenFile;
  return result;
}

main();
